import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShoppingBasket, X, ChevronRight, Trash2, CalendarPlus } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { useBasket, BASKET_MAX } from "@/context/BasketContext";

/**
 * Floating booking-basket button (bottom-right on every public page) that
 * expands to a Sheet-style drawer listing the picked candidates. Clicking
 * "Book together" navigates to /candidates/{first}/book?basket=1 — Booking.jsx
 * then auto-fills the remaining candidates from the localStorage basket.
 *
 * The FAB hides itself on the booking page itself (avoid stealing focus from
 * the actual booking form) and on admin/auth pages.
 */
export default function BasketSidebar() {
  const { items, count, remove, clear } = useBasket();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Suppress on booking / admin / auth pages — the basket is a *catalog* helper.
  const suppress =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/employer/") ||
    location.pathname.startsWith("/portal") ||
    location.pathname.startsWith("/forgot-password") ||
    location.pathname.startsWith("/reset-password") ||
    /^\/candidates\/[^/]+\/book$/.test(location.pathname);
  if (suppress) return null;

  const goBook = () => {
    if (count === 0) { toast.error(t("basket.empty", "Basket is empty")); return; }
    const first = items[0].id;
    setOpen(false);
    navigate(`/candidates/${first}/book?basket=1`);
  };

  return (
    <>
      {/* Floating action button — stacked ABOVE the WhatsApp FAB so both remain
          reachable. Mobile: bottom-40 (WA is at bottom-24). Desktop: bottom-24
          (WA is at bottom-6). Uses z-45 so it also floats above WA if they overlap. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Booking basket"
        style={{ zIndex: 45 }}
        className={
          "fixed bottom-40 sm:bottom-24 right-6 md:right-8 h-14 w-14 rounded-full shadow-xl border-2 border-zinc-950 " +
          "flex items-center justify-center transition-all duration-300 " +
          (count > 0
            ? "bg-[#b8860b] text-zinc-950 hover:scale-110 hover:bg-[#d4a017]"
            : "bg-white text-zinc-800 hover:scale-110 hover:bg-zinc-50")
        }
        data-testid="basket-fab"
      >
        <ShoppingBasket size={22} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-zinc-950 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white"
            data-testid="basket-count-badge"
          >
            {count}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-40 animate-in fade-in duration-200"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="fixed z-50 right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
            data-testid="basket-drawer"
          >
            <header className="flex items-center justify-between px-6 py-5 border-b border-zinc-200">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#b8860b] font-semibold">
                  {t("basket.overline", "Booking Basket")}
                </div>
                <h3 className="font-serif text-2xl text-zinc-950 leading-tight mt-0.5">
                  {t("basket.title", "{{n}} of {{max}} candidates", { n: count, max: BASKET_MAX })}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close basket"
                className="p-2 rounded-full hover:bg-zinc-100 transition"
                data-testid="basket-close"
              ><X size={18} /></button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {count === 0 ? (
                <div className="text-center py-16" data-testid="basket-empty">
                  <div className="w-16 h-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
                    <ShoppingBasket size={24} className="text-[#b8860b]" />
                  </div>
                  <div className="font-serif text-xl text-zinc-950">{t("basket.empty_title", "Your basket is empty")}</div>
                  <p className="text-sm text-zinc-600 mt-2 max-w-xs mx-auto leading-relaxed">
                    {t("basket.empty_desc", "Browse the catalog and tap “Add to booking” on up to {{n}} candidates to book them together.", { n: BASKET_MAX })}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate("/catalog"); }}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-950 text-sm font-medium hover:bg-zinc-950 hover:text-white transition"
                    data-testid="basket-browse-btn"
                  >
                    {t("basket.browse", "Browse Catalog")} <ChevronRight size={14} />
                  </button>
                </div>
              ) : (
                <ul className="space-y-3">
                  {items.map((c, i) => (
                    <li
                      key={c.id}
                      className="flex items-center gap-3 p-3 rounded-2xl border border-zinc-200 bg-zinc-50/40 hover:border-[#b8860b] transition"
                      data-testid={`basket-item-${c.id}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-[#b8860b] text-zinc-950 text-[11px] font-bold flex items-center justify-center shrink-0">
                        {i + 1}
                      </div>
                      {c.photo_url ? (
                        <img src={resolveMediaUrl(c.photo_url)} alt={c.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 font-serif text-lg shrink-0">
                          {(c.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-zinc-950 truncate">{c.name}</div>
                        <div className="text-xs text-zinc-600 truncate">{c.role}{c.nationality ? ` · ${c.nationality}` : ""}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(c.id)}
                        aria-label={`Remove ${c.name}`}
                        className="p-2 rounded-full text-zinc-400 hover:text-red-700 hover:bg-red-50 transition"
                        data-testid={`basket-remove-${c.id}`}
                      ><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {count > 0 && (
              <footer className="border-t border-zinc-200 px-6 py-4 space-y-2 bg-zinc-50/50">
                <button
                  type="button"
                  onClick={goBook}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#b8860b] text-zinc-950 font-semibold text-sm hover:bg-[#d4a017] transition"
                  data-testid="basket-book-btn"
                >
                  <CalendarPlus size={16} />
                  {count === 1
                    ? t("basket.book_one", "Book this interview")
                    : t("basket.book_many", "Book {{n}} interviews together", { n: count })}
                </button>
                <button
                  type="button"
                  onClick={() => { if (window.confirm(t("basket.clear_confirm", "Clear the basket?"))) clear(); }}
                  className="w-full text-xs text-zinc-500 hover:text-red-700 transition py-1"
                  data-testid="basket-clear-btn"
                >
                  {t("basket.clear", "Clear all")}
                </button>
              </footer>
            )}
          </aside>
        </>
      )}
    </>
  );
}
